import React, { useEffect, useMemo } from 'react';
import { getLanguage, getPolyglot } from '../../../../i18n';
import {
  Box,
  Container,
  Divider,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import {
  CHANNEL_TYPE,
  ChannelMap,
  ReportRequest,
  ReportType,
} from '../../../../redux/reports/api/Report.model';
import moment from 'moment';
import { getMyWorkspaceIdSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { useAppSelector } from '../../../../redux/store.model';
import _ from 'lodash';
import { useGroupById } from '../../../../redux/groups/hooks/useGroupById';
import * as Yup from 'yup';
import HelperText from '../../../../theme/components/Forms/HelperText';
import LoadingButton from '@mui/lab/LoadingButton';
import { InfoModalProps } from '../../../../theme/components';
import { GroupCreateAPI } from '../../../../redux/groups/api/group.model';
import ReportAPI from '../../../../redux/reports/api/ReportAPI';
import ChannelsMapTable from './ChannelsMapTable';
import { useShowErrorMessage } from '../../../../handlingErrors';
import usePreferredTemperatureUnit from '../../../../redux/settings/hooks/usePreferredTemperatureUnit';

const LIST_ANATOMY_COMPONENTS_UNSELECTED_BY_DEFAULT = ['parameters', 'alarms'];

export const getInitialValuesReportSpec = (
  firstReportType: ReportType | undefined,
  group: GroupCreateAPI | undefined,
  preferredTemperatureUnitString?: string
): Omit<ReportRequest, 'id'> => {
  const labels: Record<string, string> = {};
  if (preferredTemperatureUnitString == '°F') {
    labels['temperatureUnitPreferred'] = 'DEGREEFAHRENHEIT';
  }
  return {
    reportTitle: '',
    reportTypeId: firstReportType?.id,
    channels:
      group?.composableDevice?.channels
        ?.filter((channel) => channel.channelType === 'sensordata')
        // just get the first channel as default
        .slice(0, 1)
        .map<ChannelMap>((channel) => ({
          composableDeviceId: group?.id,
          channelID: channel.channel,
          cuID: channel.controlUnitId,
          deviceID: channel?.deviceId,
          type: CHANNEL_TYPE.ANALOG,
        })) ?? [],
    anatomyComponentsEnabled:
      firstReportType?.anatomyComponents?.filter(
        (component) =>
          !LIST_ANATOMY_COMPONENTS_UNSELECTED_BY_DEFAULT.includes(component)
      ) ?? [],
    intervalTimeInSeconds: 120,
    exportFormats: ['pdf'],
    timeRange: {
      from: moment().subtract(1, 'week').format('YYYY-MM-DDTHH:mm'),
      to: moment().format('YYYY-MM-DDTHH:mm'),
    },
    locales: [getLanguage(), ...(navigator.languages ?? [])],
    labels,
    timezone: moment.tz.guess(),
  };
};

const validationSchema = Yup.object().shape({
  reportTitle: Yup.string().required('reports.report_title_required'),
  channels: Yup.array().min(1, 'reports.channel_map_required'),
  timeRange: Yup.object().shape({
    from: Yup.date()
      .required('reports.schedule.from_required')

      .max(Yup.ref('to'), 'reports.schedule.from_must_be_lower')
      .test(
        'max-diff-1-month',
        'reports.schedule.max_diff_1_month',
        function (value) {
          if (!value || !this.parent.to || !value) return true;
          const from = moment(value);
          const to = moment(this.parent.to);
          return Math.abs(to.diff(from, 'months', true)) <= 1;
        }
      ),

    to: Yup.date()
      .required('reports.schedule.to_required')
      .min(Yup.ref('from'), 'reports.schedule.to_must_be_greater')
      .test('to_invalid', 'reports.schedule.to_invalid', (value) => {
        if (!value) return true;
        return moment(value).isBefore(moment().add(1, 'minute'));
      })
      .test(
        'max-diff-1-month',
        'reports.schedule.max_diff_1_month',
        function (value) {
          if (!value || !this.parent.from || !value) return true;
          const from = moment(this.parent.from);
          const to = moment(value);
          return Math.abs(to.diff(from, 'months', true)) <= 1;
        }
      ),
  }),
  intervalTimeInSeconds: Yup.number()
    .min(120, 'reports.interval_time_minimum')
    .required('reports.interval_time_required'),
});
export interface CreateReportProps extends InfoModalProps {
  groupId?: string;
}
function CreateReport(props: CreateReportProps) {
  const { groupId } = props;
  const polyglot = getPolyglot();
  const showErrorMessage = useShowErrorMessage();
  const workspaceId = useAppSelector(getMyWorkspaceIdSelector) ?? '';
  const { data: reportTypes } =
    ReportAPI.endpoints.getReportTypes.useQuery(workspaceId);
  const firstReportType = _.first(reportTypes);
  const { data: group, isLoading: isLoadingGroup } = useGroupById(groupId);
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const initialValues = useMemo(
    () =>
      getInitialValuesReportSpec(
        firstReportType,
        group,
        preferredTemperatureUnit
      ),
    [firstReportType, group, preferredTemperatureUnit]
  );

  const [createReport, { isLoading: isCreatingReport }] =
    ReportAPI.endpoints.createReport.useMutation();

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      values.reportType = reportTypes?.find(
        (type) => type.id === values.reportTypeId
      );
      return await createReport({ spec: values, workspaceId })
        .unwrap()
        .then(props.onClose)
        .catch(showErrorMessage);
    },
  });
  useEffect(() => {
    formik.resetForm();
  }, [props.open]);

  const reportTypeSelected = useMemo(
    () => reportTypes?.find((type) => type.id === formik.values.reportTypeId),
    [reportTypes, formik.values.reportTypeId]
  );

  return (
    <Container maxWidth="md" disableGutters sx={{ py: 2 }}>
      <Typography variant="h2" mb={2}>
        {polyglot.t('reports.create_report')}
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} mb={2}>
          <TextField
            label={polyglot.t('reports.type.title')}
            value={formik.values.reportTypeId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="reportTypeId"
            required
            error={Boolean(
              formik.touched.reportTypeId && formik.errors.reportTypeId
            )}
            helperText={
              formik.touched.reportTypeId && formik.errors.reportTypeId
                ? polyglot.t(formik.errors.reportTypeId)
                : undefined
            }
            select
          >
            {reportTypes?.map((reportType) => (
              <MenuItem key={reportType.id} value={reportType.id}>
                {reportType.name}
              </MenuItem>
            )) ?? <MenuItem value={''}></MenuItem>}
          </TextField>
          <TextField
            label={polyglot.t('reports.report_title')}
            value={formik.values.reportTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="reportTitle"
            required
            error={Boolean(
              formik.touched.reportTitle && formik.errors.reportTitle
            )}
            helperText={
              formik.touched.reportTitle && formik.errors.reportTitle
                ? polyglot.t(formik.errors.reportTitle)
                : polyglot.t('reports.report_helper_text')
            }
          />
          <FormGroup>
            <TextField
              label={polyglot.t('reports.interval_sample')}
              value={formik.values.intervalTimeInSeconds}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="intervalTimeInSeconds"
              type="number"
              error={Boolean(
                formik.touched.intervalTimeInSeconds &&
                  formik.errors.intervalTimeInSeconds
              )}
              helperText={
                formik.touched.intervalTimeInSeconds &&
                formik.errors.intervalTimeInSeconds
                  ? polyglot.t(formik.errors.intervalTimeInSeconds + '', {
                      seconds: 120,
                    })
                  : polyglot.t('reports.interval_sample_helper_text')
              }
              required
            />
          </FormGroup>

          <TextField
            label={polyglot.t('reports.anatomyComponents.enable_label')}
            value={formik.values.anatomyComponentsEnabled}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="anatomyComponentsEnabled"
            required
            error={Boolean(
              formik.touched.anatomyComponentsEnabled &&
                formik.errors.anatomyComponentsEnabled
            )}
            helperText={
              formik.touched.anatomyComponentsEnabled &&
              formik.errors.anatomyComponentsEnabled
                ? polyglot.t(formik.errors.anatomyComponentsEnabled)
                : polyglot.t('reports.anatomyComponents.enable_help_text')
            }
            select
            SelectProps={{
              multiple: true,
            }}
          >
            {reportTypeSelected?.anatomyComponents?.map((component) => (
              <MenuItem key={component} value={component}>
                {polyglot.t(`reports.anatomyComponents.labels.${component}`)}
              </MenuItem>
            )) ?? <MenuItem value={''}></MenuItem>}
          </TextField>

          <TextField
            label={polyglot.t('user.temperature_unit')}
            value={formik.values.labels?.temperatureUnitPreferred || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="labels.temperatureUnitPreferred"
            select
          >
            <MenuItem value={'DEGREECELCIUS'}>{'°C'}</MenuItem>
            <MenuItem value={'DEGREEFAHRENHEIT'}>{'°F'}</MenuItem>
          </TextField>

          <Box>
            <ChannelsMapTable
              formik={formik}
              groupId={groupId}
              variant="report"
            />
            {formik.touched.channels && formik.errors.channels && (
              <HelperText color="error">
                {polyglot.t(formik.errors.channels + '')}
              </HelperText>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          {/* Interval settings */}
          <Typography variant="h6" mt={2}>
            {polyglot.t('reports.period')}
          </Typography>

          <TextField
            label={polyglot.t('reports.schedule.from')}
            value={formik.values.timeRange.from}
            onChange={formik.handleChange}
            fullWidth
            onBlur={formik.handleBlur}
            name="timeRange.from"
            type="datetime-local"
            required
            error={Boolean(
              formik.touched.timeRange?.from && formik.errors.timeRange?.from
            )}
            helperText={
              formik.touched.timeRange?.from && formik.errors.timeRange?.from
                ? polyglot.t(formik.errors.timeRange.from + '')
                : undefined
            }
          />

          <TextField
            label={polyglot.t('reports.schedule.to')}
            value={formik.values.timeRange.to}
            onChange={formik.handleChange}
            fullWidth
            onBlur={formik.handleBlur}
            name="timeRange.to"
            type="datetime-local"
            required
            error={Boolean(
              formik.touched.timeRange?.to && formik.errors.timeRange?.to
            )}
            helperText={
              formik.touched.timeRange?.to && formik.errors.timeRange?.to
                ? polyglot.t(formik.errors.timeRange.to + '')
                : undefined
            }
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={isCreatingReport}
            >
              {polyglot.t('button.save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Container>
  );
}

export default CreateReport;
